import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, Lock, CheckCircle2, Clock, Award } from 'lucide-react';
import { learningPathsAPI } from '../../services/api';

const labelize = (value) => String(value || '').replace(/_/g, ' ');

export default function LearningPathsPage() {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPaths = async () => {
      try {
        const data = await learningPathsAPI.getMy();
        setPaths(data.learningPaths || []);
      } catch (error) {
        console.error('Failed to fetch learning paths:', error);
      } finally {
        setLoading(false);
      }
    };
    void loadPaths();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[20rem] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-display font-bold text-gray-900">
          <GitBranch className="h-6 w-6 text-primary-600" />
          My Learning Paths
        </h1>
        <p className="mt-1 text-gray-600">
          Track multi-course programs, bundle completion, and sequence locks in one place.
        </p>
      </div>

      {paths.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center">
          <GitBranch className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-900">No learning paths assigned</h2>
          <p className="mt-1 text-sm text-gray-500">
            When a path is assigned to you, it will appear here with progress and locked steps.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {paths.map((pathEnrollment) => (
            <div key={pathEnrollment._id} className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                      {labelize(pathEnrollment.mode)}
                    </span>
                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                      {labelize(pathEnrollment.status)}
                    </span>
                  </div>
                  <h2 className="mt-3 text-xl font-semibold text-gray-900">
                    {pathEnrollment.learningPathId?.title || 'Learning Path'}
                  </h2>
                  {pathEnrollment.dueDate && (
                    <p className="mt-1 flex items-center gap-1 text-sm text-gray-500">
                      <Clock size={14} />
                      Due {new Date(pathEnrollment.dueDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="rounded-xl bg-primary-50 px-4 py-3 text-center">
                  <p className="text-2xl font-bold text-primary-700">
                    {pathEnrollment.progress?.percentage || 0}%
                  </p>
                  <p className="text-xs font-medium uppercase tracking-wide text-primary-600">
                    Complete
                  </p>
                </div>
              </div>

                <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{ width: `${pathEnrollment.progress?.percentage || 0}%` }}
                  />
                </div>

              {pathEnrollment.certificate?.certificateId && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  <Award size={16} />
                  Path certificate issued
                </div>
              )}

              <div className="mt-6 grid gap-3">
                {(pathEnrollment.items || [])
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((item) => {
                    const blocked = item.status === 'blocked';
                    const completed = item.status === 'completed';
                    const courseId = item.courseId?._id || item.courseId;
                    return (
                      <div
                        key={`${pathEnrollment._id}-${courseId}`}
                        className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">Step {item.order}</p>
                          <p className="text-sm text-gray-500">
                            {item.required ? 'Required' : 'Optional'} · {labelize(item.status)}
                          </p>
                          {blocked && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                              <Lock size={12} />
                              {item.blockedReason || 'Complete earlier required steps to unlock this course.'}
                            </p>
                          )}
                          {item.isOverdue && (
                            <p className="mt-1 text-xs text-rose-700">
                              Overdue by {item.overdueByDays || 1} day{item.overdueByDays === 1 ? '' : 's'}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {completed && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                          {item.enrollmentId ? (
                            <Link
                              to={`/training/${courseId}`}
                              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                                blocked
                                  ? 'cursor-not-allowed bg-gray-100 text-gray-400 pointer-events-none'
                                  : 'bg-primary-600 text-white hover:bg-primary-700'
                              }`}
                            >
                              {completed ? 'Review Course' : blocked ? 'Locked' : 'Open Course'}
                            </Link>
                          ) : (
                            <span className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-500">
                              Not enrolled yet
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
